import React, { useState, useRef, useEffect } from 'react';
import './index.module.less';

import NavBar from '@components/NavBar';

import { Api } from '@apis/client';
import Http from '@helpers/Http';
import userStore from '@stores/user';
import { IDeviceBindInfo } from '@apis/device/getMyDevices';
import SwiperCore, { Pagination, Scrollbar, A11y } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/swiper-bundle.css';
import 'swiper/components/pagination/pagination.scss';
import 'swiper/components/scrollbar/scrollbar.scss';
import MyDevicePage from './MyDevicePage/index';
import ConfirmModal from '@views/components/ConfirmModal';
import PageNavigator from '@helpers/PageNavigator';
import NativeInterface from '@helpers/NativeInterface';
import BrowserHistory from '@helpers/BrowserHistory';
import { isCubeApp, paths } from '@helpers/Constants';
import EmptyView from '@views/sections/EmptyView';
import FridayService from '@services/friday';

SwiperCore.use([Pagination, Scrollbar, A11y]);

const ellipsisText = (text: string, limitLength: number) => {
  if (limitLength <= 0 || text.length <= limitLength) {
    return text;
  }
  return text.substring(0, limitLength) + '...';
}

const MyDevices = () => {

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [deviceList, setDeviceList] = useState<IDeviceBindInfo[]>(null);
  const swiperRef = useRef<any>();
  const tabsRef = useRef<HTMLDivElement>(null);

  const renderSwiperTab = (value: string, index: number) => {
    const textStyle = selectedIndex === index ? 'text active' : 'text';
    return <div key={index} styleName="custom-swiper-tab" onClick={() => {
      handleSelecteIndex(index);
      swiperRef?.current?.slideTo(index);
    }}>
      <div styleName={textStyle}>{value}</div>
    </div>;
  };

  const renderSwiperTabs = (tabs: string[]) => {
    let tabList = tabs.map((item: string, index: number) => {
      return renderSwiperTab(item, index);
    });

    return <div ref={tabsRef} styleName="custom-swiper-tabs">{tabList}</div>;
  };

  const handleSelecteIndex = (index: number) => {
    setSelectedIndex(index);
    if (deviceList && deviceList[index]) {
      const snCode = deviceList[index].snCode;
      const hrefObj = new window.URL(window.location.href);
      hrefObj.searchParams.set('selectedDeviceSN', `${snCode}`);
      window.history.replaceState(null, null, hrefObj.href);
    }
  };

  useEffect(() => {

    const getMyDeviceList = async () => {
      const result = await Http.post(Api.GetMyDevices, {
        userUid: userStore.userInfo.uid,
      });
      const resultDevices = result?.data || [];
      setDeviceList(resultDevices);

      // 根据 URL 入参设置选中的设备
      const hrefObj = new window.URL(window.location.href);
      if (hrefObj.searchParams?.has('selectedDeviceSN')) {
        const paramSNCode = hrefObj.searchParams?.get('selectedDeviceSN');
        const targetIndex = resultDevices.findIndex((element) => { return element.snCode === paramSNCode });
        if (targetIndex !== -1) {
          handleSelecteIndex(targetIndex);
          swiperRef?.current?.slideTo(targetIndex);
          autoJustTabPosition(targetIndex);

          if (hrefObj.searchParams?.has('showLocationWindow')) {
            hrefObj.searchParams.delete('showLocationWindow');
            window.history.replaceState(null, null, hrefObj.href);
            setTimeout(() => {
              ConfirmModal.show({
                title: '温馨提示',
                showCancelBtn: true,
                cancelBtnText: '暂不开启',
                confirmBtnText: '同 意',
                onConfirm: () => onAgreeLocationProtocol(resultDevices[targetIndex]),
                ContentWidget: (
                  <div className='flex' styleName='location-window'>
                    <p styleName='sub-title'>欢迎使用希沃电子学生证定位服务</p>
                    <p>1. 为向您提供实时查看孩子的精准位置信息、行踪轨迹信息的服务，我们需要获取您授权提供的您孩子的位置信息（包括动态位置信息、精准定位信息、行踪轨迹信息）以及运动信息（包括步数、步频、运动距离、运动时间、运动速度）。</p>
                    <p>2. 如您拒绝提供授权，仅会使您无法正常使用希沃学生证设备的定位服务，但不影响希沃魔方及希沃学生证设备的其他功能正常使用。</p>
                    <p>3. 您可随时通过【定位-定位设置】自主选择开启或关闭定位服务。</p>
                    <p styleName='link'>请仔细阅读<span onClick={showProtocol1}>《授权使用声明》</span>，并确认是否同意<span onClick={showProtocol2}>《个人信息使用授权同意书》</span>的内容。</p>
                  </div>)
              });
            }, 500);
          }
        }
      }
    };

    getMyDeviceList();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showProtocol1 = () => {
    PageNavigator.to({ url: 'https://account.seewo.com/seewo-account/agreement?appCode=seewo-yunban-mobile&type=1&index=4' });
  }

  const showProtocol2 = () => {
    PageNavigator.to({ url: 'https://account.seewo.com/seewo-account/agreement?appCode=seewo-yunban-mobile&type=1&index=3' });
  }

  const onAgreeLocationProtocol = (deviceInfo: IDeviceBindInfo) => {
    Http.post(Api.PostDeviceSwitchStatus,
      { snCode: deviceInfo.snCode, company: deviceInfo.company, key: 'locationSwitch', value: '1' }
    )
  }

  const navBarTitle = (!deviceList || deviceList.length === 0 || deviceList.length === 1) ? '我的设备' : `我的设备(${deviceList.length})`;
  const tabs = deviceList?.map((item: IDeviceBindInfo, index: number) => {
    let deviceSNCode = item.snCode;
    if (deviceSNCode.length >= 4) {
      deviceSNCode = deviceSNCode.substring(deviceSNCode.length - 4, deviceSNCode.length);
    }
    let tabText = (item.bindUserName || `学生证 ${deviceSNCode}`);
    tabText = ellipsisText(tabText, 10);
    return tabText;
  });

  const pageList = deviceList?.map((item: IDeviceBindInfo, index: number) => {
    const visible = index === selectedIndex;
    return <SwiperSlide key={index}><MyDevicePage info={item} visiable={visible} /></SwiperSlide>;
  });

  const toScanCode = () => {
    FridayService.addEvent('bind_device_scan_self_show', {source: '我的设备'});
    if (isCubeApp()) {
      NativeInterface.jumpNative({ path: '/scan', extras: {} });
    } else {
      BrowserHistory.push(`${PUBLIC_URL}${paths.scan}`);
    }
  };

  const renderNavRightComponent = () => {
    return (<i
      className="iconfont icon-saoyisao"
      styleName="nav-icon"
      onClick={toScanCode}
    />);
  }

  const autoJustTabPosition = (index: number) => {
    const targetIndex = index;
    const targetChild = tabsRef.current.children.item(targetIndex);
    const targetChildLeft = targetChild.getBoundingClientRect().left;
    if (targetChildLeft) {
      if (targetChildLeft >= window.outerWidth) {
        tabsRef.current.scrollBy(targetChildLeft, 0);
      } else if (targetChildLeft < 0) {
        tabsRef.current.scrollTo(10, 0);
      }
    }
  }

  const hasData = deviceList && deviceList.length > 0;

  return (
    <div styleName="container" style={{ height: document.body.clientHeight }}>
      <NavBar title={navBarTitle} RightBtnComponent={renderNavRightComponent()} />
      {
        hasData &&
        <div styleName="custom-swiper">
          {deviceList?.length > 1 ? renderSwiperTabs(tabs) : null}
          <Swiper
            onInit={(swiper: any) => {
              swiperRef.current = swiper;
            }}
            onSlideChange={(swiper: any) => {
              handleSelecteIndex(swiper.activeIndex);
              autoJustTabPosition(swiper.activeIndex);
            }}>
            {pageList}
          </Swiper>
        </div>
      }
      {
        (deviceList?.length === 0) && <EmptyView
          type='device'
          title={'还没有添加学生证哦~'}
          desc={'扫描学生证上的二维码添加'}
          button={'添加设备'}
          onClickButton={toScanCode}
        />
      }
    </div>
  );
};

export default MyDevices;
